import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import SiteFooter from "@/components/SiteFooter";
import {
  ArrowRight, Users2, MessageSquare, Lock, Radio, Tag, ListChecks,
} from "lucide-react";

// iter-61 — Public /workspaces landing page. Distinct from the authenticated
// /library/workspaces manager — this markets the enterprise-collaboration
// story to visiting teams before signup.

const REALTIME_FEATURES = [
  { icon: Radio, title: "Live multi-user cursors",
    body: "Built on real-time WebSocket channels. See every editor's cursor, typing indicator, and avatar the moment they join a book." },
  { icon: Lock, title: "Project conflict prevention",
    body: "Smart per-book locking keeps editors from saving over each other's active work blocks. Lock alerts surface across every session." },
];

const WORKFLOW_FEATURES = [
  { icon: Tag, title: "Manuscript dashboard tagging",
    body: "Tag your active catalog across Draft, Structural Editing, Cover Design, and Published milestones — plus any custom label your team defines." },
  { icon: MessageSquare, title: "Threaded inline feedback",
    body: "Attach comments to any block. Threaded replies stay organised in the sidebar so conversations don't fragment across email and Slack." },
  { icon: ListChecks, title: "Multi-admin permissions",
    body: "Assign multiple admins per workspace. Move books between personal libraries and shared workspaces without losing history." },
];

export default function WorkspacesLanding() {
  return (
    <div className="min-h-screen bg-[#faf9f6]">
      <Nav />

      {/* ─── Hero ─────────────────────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-6 pt-16 pb-14">
        <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
          Enterprise · Publisher Workspaces
        </p>
        <div className="grid grid-cols-12 gap-8 items-end mt-4">
          <div className="col-span-12 lg:col-span-8">
            <h1
              className="font-editorial font-light tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-8xl"
              data-testid="workspaces-h1"
            >
              The private studio
              <span className="block italic text-[color:var(--primary)]">
                for publishing teams.
              </span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[color:var(--text-muted)]">
              Indie presses, co-authors, and structural editors share one
              catalogue with live presence, per-book locks, and threaded
              feedback. No more "who has the latest DOCX?" — the latest
              file lives in BookForge.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/register"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="workspaces-cta-primary"
              >
                Scale your team <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link
                to="/#pricing"
                className="btn-ghost"
                data-testid="workspaces-cta-pricing"
              >
                See publisher pricing
              </Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <div
              className="bg-white border border-[color:var(--border)] p-6 rounded-lg shadow-[0_20px_40px_-25px_rgba(0,0,0,0.2)]"
              data-testid="workspaces-preview-card"
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] mb-3">
                Nervous System · Draft
              </div>
              <div className="space-y-2 mb-4">
                {[
                  { name: "Richard", role: "Author", color: "bg-[#D95D39]" },
                  { name: "Priya", role: "Editor · typing", color: "bg-[#3f8f8f]" },
                  { name: "Marc", role: "Reviewer", color: "bg-[#5b6ab0]" },
                ].map((u) => (
                  <div key={u.name} className="flex items-center gap-3 text-sm">
                    <div className={`w-8 h-8 rounded-full ${u.color} text-white flex items-center justify-center text-xs font-medium`}>
                      {u.name[0]}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{u.name}</div>
                      <div className="text-[11px] text-[color:var(--text-muted)]">
                        {u.role}
                      </div>
                    </div>
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                  </div>
                ))}
              </div>
              <div className="pt-3 border-t border-[color:var(--border)] text-[11px] text-[color:var(--text-muted)] flex items-center gap-2">
                <Users2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                3 active · Locked by Priya
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Real-time collaborative environment ─────────────────────── */}
      <section
        className="border-t border-[color:var(--border)] bg-white"
        data-testid="workspaces-realtime"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
              § 01 — Real-time collaboration
            </p>
            <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
              Everyone in the same room,{" "}
              <em className="text-[color:var(--primary)]">no matter the timezone.</em>
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {REALTIME_FEATURES.map((f, idx) => (
              <div
                key={f.title}
                className="bg-[#faf9f6] border border-[color:var(--border)] p-8 transition-all duration-300 hover:border-[color:var(--text)]"
                data-testid={`workspaces-realtime-card-${idx}`}
              >
                <f.icon
                  className="w-6 h-6 mb-4 text-[color:var(--primary)]"
                  strokeWidth={1.5}
                />
                <h3 className="font-editorial text-2xl mb-2">{f.title}</h3>
                <p className="text-[color:var(--text-muted)] leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Production tracking & workflow ─────────────────────────── */}
      <section
        className="border-t border-[color:var(--border)]"
        data-testid="workspaces-workflow"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
              § 02 — Production tracking
            </p>
            <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
              Every book, every milestone,{" "}
              <em className="text-[color:var(--primary)]">one dashboard.</em>
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {WORKFLOW_FEATURES.map((f, idx) => (
              <div
                key={f.title}
                className="bg-white border border-[color:var(--border)] p-8 transition-all duration-300 hover:border-[color:var(--text)]"
                data-testid={`workspaces-workflow-card-${idx}`}
              >
                <f.icon
                  className="w-6 h-6 mb-4 text-[color:var(--primary)]"
                  strokeWidth={1.5}
                />
                <h3 className="font-editorial text-2xl mb-2">{f.title}</h3>
                <p className="text-[color:var(--text-muted)] leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Bottom CTA ─────────────────────────────────────────────── */}
      <section className="border-t border-[color:var(--border)] bg-[#f4f2ec]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">
          <h2 className="font-editorial text-4xl sm:text-5xl leading-tight">
            Ship faster, together.
          </h2>
          <p className="mt-6 text-lg max-w-2xl mx-auto text-[color:var(--text-muted)]">
            Start with a free trial. Upgrade to Publisher when your team's
            ready — no data migration, no re-setup, just more seats.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/register"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="workspaces-bottom-cta"
            >
              Create your workspace <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link
              to="/features"
              className="btn-ghost"
              data-testid="workspaces-bottom-features"
            >
              Explore the engine
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
